'use client'

import type { ChangeEvent } from 'react'
import { Check } from 'lucide-react'
import clsx from 'clsx'

import Error from '/react4xp/components/prototype/Form/Error'
import Tooltip from '/react4xp/components/prototype/Form/Tooltip'
import { useFormField } from '/react4xp/utils/prototype/useFormField'

import type { CheckboxProps } from './types'

export default function Checkbox({
	id,
	name,
	label,
	labelPosition = 'top',
	required = false,
	disabled = false,
	error: externalError,
	invalid: externalInvalid,
	className,
	onChange: externalOnChange,
	requiredMessage,
	tooltipLabel,
	...props
}: CheckboxProps) {
	const { finalError, finalInvalid, updateFormValue, getRegisterProps } = useFormField({
		name,
		externalError,
		externalInvalid,
		required,
		requiredMessage,
	})
	const errorId = `${id}-error`

	const registerProps = getRegisterProps()

	function handleChange(e: ChangeEvent<HTMLInputElement>) {
		externalOnChange?.(e.target.checked)
		updateFormValue(e.target.checked ? 'true' : '')
	}

	return (
		<div className="form-field">
			<div className="flex items-center gap-1.5">
				<label htmlFor={id} data-label-position={labelPosition} className={clsx('form-checkbox-row', className)}>
					<input
						{...registerProps}
						type="checkbox"
						id={id}
						name={name}
						required={required}
						disabled={disabled}
						aria-invalid={finalInvalid || undefined}
						aria-describedby={finalError ? errorId : undefined}
						className="form-checkbox-input"
						onChange={handleChange}
						{...props}
					/>
					<span className="form-checkbox-box">
						<Check className="form-checkbox-check" />
					</span>
					{label && (
						<span
							className="form-checkbox-label"
							dangerouslySetInnerHTML={{ __html: label }}
							suppressHydrationWarning
						/>
					)}
				</label>
				{tooltipLabel && <Tooltip label={tooltipLabel} />}
			</div>
			{finalError && finalInvalid && <Error label={finalError} id={errorId} />}
		</div>
	)
}
